import type {
  PnLWaterfall,
  DrawdownPath,
  DrawdownPoint,
  AdjustmentDeltas,
} from '@/types/api'

// =============================================================================
// Type Definitions
// =============================================================================

export interface WaterfallDataPoint {
  name: string
  value: number
  isTotal?: boolean
  fill?: string
  invisible?: number
  visible?: number
  level: number // 0 = portfolio, 1 = bucket, 2 = position
  assetClass?: string
}

export interface DrawdownDataPoint {
  date: string
  value: number
  nav: number
  drawdown: number
  isPeak?: boolean
  isTrough?: boolean
  isRecovery?: boolean
  label?: string
}

export interface DeltaRow {
  adjustmentName: string
  baselinePnL: number
  adjustedPnL: number
  deltaPnL: number
  deltaPercentage: number
  isPositive: boolean
  keyDrivers: string[]
}

// =============================================================================
// Decimal String Parsers
// =============================================================================

/**
 * Parse a decimal string to a number.
 * Returns 0 if parsing fails.
 */
export function parseDecimalString(value: string | null | undefined): number {
  if (!value) return 0
  const parsed = parseFloat(value)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Parse an object with decimal string values to numbers.
 * Recursively processes nested objects.
 */
export function parseDecimalStrings<T extends Record<string, unknown>>(
  obj: T
): Record<string, number | unknown> {
  const result: Record<string, number | unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      // Check if it looks like a decimal (contains digits and optionally a decimal point)
      if (/^-?\d+\.?\d*$/.test(value)) {
        result[key] = parseDecimalString(value)
      } else {
        result[key] = value
      }
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[key] = parseDecimalStrings(value as Record<string, unknown>)
    } else {
      result[key] = value
    }
  }

  return result
}

// =============================================================================
// Formatting Helpers
// =============================================================================

/**
 * Format a number as currency ($X,XXX.XX).
 */
export function formatCurrency(value: number): string {
  const absValue = Math.abs(value)
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absValue)

  return value < 0 ? `-${formatted}` : formatted
}

/**
 * Format a number as percentage (XX.XX%).
 */
export function formatPercentage(value: number): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100)

  return formatted
}

/**
 * Format a number as compact currency (e.g., $1.2M, $850K).
 */
export function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 1_000_000_000) {
    return `${sign}$${(absValue / 1_000_000_000).toFixed(1)}B`
  } else if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`
  } else if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1)}K`
  } else {
    return `${sign}$${absValue.toFixed(2)}`
  }
}

// =============================================================================
// Color Constants
// =============================================================================

export const CHART_COLORS = {
  positive: '#10b981', // emerald-500
  negative: '#ef4444', // red-500
  total: '#3b82f6', // blue-500
  neutral: '#6b7280', // gray-500
  crypto: '#8b5cf6', // violet-500
  tradfi: '#10b981', // emerald-500
}

// =============================================================================
// Waterfall Chart Transformers
// =============================================================================

/**
 * Transform PnLWaterfall data for Recharts stacked bar chart.
 * Uses the technique of invisible + visible bars to create waterfall effect.
 *
 * Pattern (per 05-RESEARCH.md):
 * - Each bar has an "invisible" base (transparent) stacked with "visible" portion
 * - Invisible height = sum of all previous values (cumulative)
 * - Visible height = actual P&L value
 * - For negative values, invisible = cumulative + value (shift down)
 */
export function transformWaterfallData(pnlWaterfall: PnLWaterfall): WaterfallDataPoint[] {
  const data: WaterfallDataPoint[] = []
  let cumulative = 0

  // 1. Portfolio Total (always first, always a total bar)
  const portfolioTotal = pnlWaterfall.portfolioTotal
  const portfolioPnL = parseDecimalString(portfolioTotal.absolutePnL)

  data.push({
    name: 'Portfolio Total',
    value: portfolioPnL,
    isTotal: true,
    fill: CHART_COLORS.total,
    invisible: 0,
    visible: portfolioPnL,
    level: 0,
  })

  cumulative = portfolioPnL

  // 2. Asset Class Buckets and their Positions
  for (const bucket of pnlWaterfall.buckets) {
    const bucketPnL = parseDecimalString(bucket.absolutePnL)
    const bucketName = bucket.assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'

    // Add bucket as a data point (intermediate total)
    data.push({
      name: `${bucketName} Bucket`,
      value: bucketPnL,
      isTotal: false,
      fill: bucketPnL >= 0 ? CHART_COLORS.positive : CHART_COLORS.negative,
      invisible: Math.min(cumulative, cumulative + bucketPnL),
      visible: Math.abs(bucketPnL),
      level: 1,
      assetClass: bucket.assetClass,
    })

    // Add individual positions within this bucket
    for (const position of bucket.positions) {
      const positionPnL = parseDecimalString(position.absolutePnL)

      data.push({
        name: position.symbol,
        value: positionPnL,
        isTotal: false,
        fill: positionPnL >= 0 ? CHART_COLORS.positive : CHART_COLORS.negative,
        invisible: Math.min(cumulative, cumulative + positionPnL),
        visible: Math.abs(positionPnL),
        level: 2,
        assetClass: bucket.assetClass,
      })

      cumulative += positionPnL
    }
  }

  return data
}

/**
 * Transform PnLWaterfall data for a simpler view (buckets only, no positions).
 * Useful when there are many positions.
 */
export function transformWaterfallBucketsOnly(pnlWaterfall: PnLWaterfall): WaterfallDataPoint[] {
  const data: WaterfallDataPoint[] = []
  let cumulative = 0

  // Portfolio Total
  const portfolioPnL = parseDecimalString(pnlWaterfall.portfolioTotal.absolutePnL)
  data.push({
    name: 'Portfolio Total',
    value: portfolioPnL,
    isTotal: true,
    fill: CHART_COLORS.total,
    invisible: 0,
    visible: portfolioPnL,
    level: 0,
  })

  cumulative = portfolioPnL

  // Buckets only
  for (const bucket of pnlWaterfall.buckets) {
    const bucketPnL = parseDecimalString(bucket.absolutePnL)
    const bucketName = bucket.assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'

    data.push({
      name: bucketName,
      value: bucketPnL,
      isTotal: false,
      fill: bucketPnL >= 0 ? CHART_COLORS.positive : CHART_COLORS.negative,
      invisible: Math.min(cumulative, cumulative + bucketPnL),
      visible: Math.abs(bucketPnL),
      level: 1,
      assetClass: bucket.assetClass,
    })

    cumulative += bucketPnL
  }

  return data
}

// =============================================================================
// Drawdown Chart Transformers
// =============================================================================

/**
 * Transform DrawdownPath data for Recharts line chart.
 * Identifies peak, trough, and recovery points for visual highlighting.
 */
export function transformDrawdownData(drawdownPath: DrawdownPath | null): DrawdownDataPoint[] {
  if (!drawdownPath || !drawdownPath.points || drawdownPath.points.length === 0) {
    return []
  }

  const maxDrawdownDate = drawdownPath.max_drawdown_date

  return drawdownPath.points.map((point: DrawdownPoint, index: number) => {
    const nav = parseDecimalString(point.nav)
    const drawdown = parseDecimalString(point.drawdown)

    // Determine if this point is the peak (highest NAV)
    const isPeak = index === 0 || (index > 0 && nav >= parseDecimalString(drawdownPath.points[0].nav))

    // Determine if this is the trough (max drawdown point)
    const isTrough = point.date === maxDrawdownDate

    // Determine if this is a recovery point (back to positive)
    const isRecovery = index > 0 &&
      drawdown === 0 &&
      parseDecimalString(drawdownPath.points[index - 1].drawdown) < 0

    let label: string | undefined
    if (isPeak) label = 'Peak'
    else if (isTrough) label = 'Trough'
    else if (isRecovery) label = 'Recovery'

    return {
      date: point.date,
      value: nav,
      nav,
      drawdown,
      isPeak,
      isTrough,
      isRecovery,
      label,
    }
  })
}

/**
 * Format date string for chart display (e.g., "Jan 2022").
 */
export function formatDrawdownDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(date)
  } catch {
    return dateString
  }
}

// =============================================================================
// Delta Comparison Transformers
// =============================================================================

/**
 * Transform AdjustmentDeltas for table display.
 * Currently handles single comparison (baseline vs adjusted).
 */
export function formatDeltaData(deltas: AdjustmentDeltas | undefined): DeltaRow[] {
  if (!deltas) {
    return []
  }

  const deltaPnL = parseDecimalString(deltas.delta_absolute)
  const deltaPercentage = parseDecimalString(deltas.delta_percentage)

  return [
    {
      adjustmentName: deltas.comparison || 'Adjusted vs Baseline',
      baselinePnL: 0, // Would need baseline data separately
      adjustedPnL: deltaPnL,
      deltaPnL,
      deltaPercentage,
      isPositive: deltaPnL >= 0,
      keyDrivers: [], // Would need position-level delta data
    },
  ]
}

/**
 * Calculate percentage change between two values.
 */
export function calculatePercentageChange(
  newValue: number,
  oldValue: number
): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0
  return ((newValue - oldValue) / Math.abs(oldValue)) * 100
}

// =============================================================================
// Coherence Score Helpers
// =============================================================================

/**
 * Get color for coherence score.
 * 80-100: Green (high coherence)
 * 50-79: Yellow (moderate)
 * 0-49: Red (low coherence)
 * null: Gray (error)
 */
export function getCoherenceColor(score: number | null): string {
  if (score === null) return CHART_COLORS.neutral
  if (score >= 80) return CHART_COLORS.positive
  if (score >= 50) return '#f59e0b' // amber-500
  return CHART_COLORS.negative
}

/**
 * Get flag text for coherence score.
 */
export function getCoherenceFlagText(
  score: number | null,
  flagText?: string
): string {
  if (flagText) return flagText
  if (score === null) return 'Coherence computation failed'
  if (score >= 80) return 'High scenario plausibility'
  if (score >= 50) return 'Moderate scenario plausibility'
  return 'Low scenario plausibility - review inputs'
}
